import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
} from "@material-tailwind/react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Toaster from "@/components/Toaster";
import { useDispatch, useSelector } from 'react-redux';
import {
    saveRfqProductMargin,
    fetchRFQProduct,
    getRfqSummary,
    getRfqById
} from "@/store/slices/rfqSlice";
import ShowLoader from "@/components/Loader";
import { getSettingData } from "@/store/slices/settingSlice";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import useHasPermission from "@/components/useHasPermission";

// Utility function to handle empty strings/nulls for number fields
const numberTransformer = (value, originalValue) =>
    String(originalValue).trim() === "" ? null : value;

export function UpdatePriceModal({
    isOpen,
    onClose,
    itemData,
    rfq_id,
    onSuccess
}) {
    const dispatch = useDispatch();
    const [price, setPrice] = useState({});
    const [activeField, setActiveField] = useState(null);

    const { t } = useTranslation('rfq');
    const { t: ct } = useTranslation("common");

    const { loading } = useSelector((state) => state.rfq);
    const { settings } = useSelector((state) => state.setting);

    const hasViewProductPurchasePricePermission = useHasPermission(
        `product-purchase-price.view`
    );

    const hasViewProductSalePricePermission = useHasPermission(
        `product-sale-price.view`
    );

    const quantity = Number(itemData?.quantity);

    const totalSalePrice =
        itemData?.sale_price &&
            Number.isFinite(Number(itemData.sale_price)) &&
            Number.isFinite(quantity)
            ? Number(itemData.sale_price) * quantity
            : null;

    const totalPurchasePrice =
        itemData?.unit_price &&
            Number.isFinite(Number(itemData.unit_price)) &&
            Number.isFinite(quantity)
            ? Number(itemData.unit_price) * quantity
            : null;

    // ---------------------------------------------------------
    // Validation Error Translation Helper
    // ---------------------------------------------------------
    const translateValidationError = (message) => {
        if (!message) return '';

        // Example:
        // max_margin_error#{"margin":30000}
        //
        // Split into:
        // key    = max_margin_error
        // params = {"margin":30000}

        const [key, params] = message.split('#');

        if (!params) {
            return t(key);
        }

        try {
            const interpolationParams = JSON.parse(params);

            return t(key, interpolationParams);
        } catch (error) {
            // Fallback in case the message is not valid JSON
            return t(key);
        }
    };

    // --- Dynamic Validation Schema ---
    const schema = yup.object().shape({
        flat_margin: yup
            .number()
            .typeError("must_be_a_number")
            .min(0, "cannot_be_negative")
            .nullable(true)
            .transform(numberTransformer)
            .when([], {
                is: () => (settings?.flat_margin || 0) > 0,
                then: (schema) =>
                    schema.max(
                        settings.flat_margin * itemData?.quantity,
                        `max_margin_error#{"margin":${settings.flat_margin * itemData?.quantity}}`
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),

        percentage_margin: yup
            .number()
            .typeError("must_be_a_number")
            .min(0, "cannot_be_negative")
            .nullable(true)
            .transform(numberTransformer)
            .when([], {
                is: () => (settings?.percentage_margin || 0) > 0,
                then: (schema) =>
                    schema.max(
                        settings.percentage_margin * itemData?.quantity,
                        `max_margin_percentage_error#{"margin":${settings.percentage_margin * itemData?.quantity}}`
                    ),
                otherwise: (schema) =>
                    schema.max(9999, "Cannot exceed 9999%"),
            }),

        flat_discount: yup
            .number()
            .typeError("must_be_a_number")
            .min(0, "cannot_be_negative")
            .nullable(true)
            .transform(numberTransformer)
            .when([], {
                is: () => (settings?.flat_discount || 0) > 0,
                then: (schema) =>
                    schema.max(
                        settings.flat_discount * itemData?.quantity,
                        `max_discount_error#{"discount":${settings.flat_discount * itemData?.quantity}}`
                    ),
                otherwise: (schema) => schema.notRequired(),
            }),

        percentage_discount: yup
            .number()
            .typeError("must_be_a_number")
            .min(0, "cannot_be_negative")
            .nullable(true)
            .transform(numberTransformer)
            .when([], {
                is: () => (settings?.percentage_discount || 0) > 0,
                then: (schema) =>
                    schema.max(
                        settings.percentage_discount * itemData?.quantity,
                        `max_discount_percentage_error#{"discount":${settings.percentage_discount * itemData?.quantity}}`
                    ),
                otherwise: (schema) =>
                    schema.max(9999, "Cannot exceed 9999%"),
            }),

        customer_price: yup
            .number()
            .typeError("must_be_a_number")
            .min(0, "cannot_be_negative")
            .required("customer_price_required")
            .test(
                'minVendorPrice',
                'customer_price_min_error',
                (value) => {
                    if (
                        itemData?.sale_price === undefined ||
                        itemData?.sale_price === null
                    ) {
                        return true;
                    }

                    return value >= itemData.sale_price;
                }
            ),
    });

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(schema),
        shouldFocusError: false,
        defaultValues: {
            flat_margin: itemData?.flat_margin || 0,
            percentage_margin: itemData?.percentage_margin || 0,
            flat_discount: itemData?.flat_discount || 0,
            percentage_discount: itemData?.percentage_discount || 0,
            customer_price:
                itemData?.customer_price ||
                itemData?.sale_price ||
                0,
        }
    });

    useEffect(() => {
        if (isOpen && itemData) {
            let data = {
                flat_margin: itemData.flat_margin || 0,
                percentage_margin: itemData.percentage_margin || 0,
                flat_discount: itemData.flat_discount || 0,
                percentage_discount: itemData.percentage_discount || 0,
                customer_price:
                    itemData.customer_price ||
                    itemData.sale_price ||
                    0,
            };

            reset(data);
            setPrice(data);
        }

        dispatch(getSettingData());

    }, [isOpen, itemData, reset]);

    const onSubmit = async (data, type) => {

        if (!itemData?.sale_price) {
            Toaster.error(t("sale_price_required_for_update"));
            return;
        }

        if (!type) return;

        const value = data?.[type];

        if (value === undefined) return;

        if (value == price[type]) return;

        const payload = {
            [type]: Number(value),
            rfq_product_id: itemData.id,
            product_id: itemData.product_id,
        };

        try {
            const result = await dispatch(
                saveRfqProductMargin(payload)
            ).unwrap();

            if (result?.status) {
                Toaster.success(
                    t('pricing_updated_success', {
                        productName: itemData?.product_name || t("item", "Item")
                    })
                );

                dispatch(
                    fetchRFQProduct({
                        rfq_id: rfq_id,
                        search: '',
                        sort_order: ''
                    })
                );

                dispatch(getRfqSummary(rfq_id));
                dispatch(getRfqById(rfq_id));

                if (onSuccess) {
                    onSuccess();
                }

                reset(result?.data);
                setPrice(result?.data);
                setActiveField(null);

                // onClose();
            } else {
                Toaster.error(
                    result.message || ct("somethingWentWrong")
                );
            }

        } catch (error) {
            Toaster.error(
                error?.message || ct("actionFailed")
            );
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            handler={handleClose}
            size="md"
            className="rounded-xl"
        >
            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                    {t('update_pricing_title')}
                </h2>

                <button
                    onClick={onClose}
                    className="p-1 rounded-full text-gray-500 hover:bg-gray-100 transition"
                >
                    <XMarkIcon className="w-6 h-6" />
                </button>
            </DialogHeader>

            <form>
                <DialogBody className="px-6 max-h-[65vh] overflow-y-auto space-y-6">

                    {/* Vendor Price Display */}
                    {(hasViewProductPurchasePricePermission ||
                        hasViewProductSalePricePermission) &&
                        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md flex justify-between gap-4">

                            <div>
                                {hasViewProductSalePricePermission &&
                                    <p className="text-sm font-semibold text-gray-700 capitalize">
                                        {t('sale_price_label')} :

                                        <span className="font-normal text-gray-900">
                                            {itemData?.sale_price
                                                ? itemData?.product?.rfq_currency_code +
                                                " " +
                                                itemData.sale_price
                                                : t('na')}
                                        </span>
                                    </p>
                                }

                                {hasViewProductPurchasePricePermission &&
                                    <p className="text-sm font-semibold text-gray-700 capitalize">
                                        {t('purchase_price_label')} :

                                        <span className="font-normal text-gray-900">
                                            {itemData?.unit_price
                                                ? itemData?.product?.rfq_currency_code +
                                                " " +
                                                itemData.unit_price
                                                : t('na')}
                                        </span>
                                    </p>
                                }
                            </div>

                            <div className="text-right">

                                {hasViewProductSalePricePermission &&
                                    <p className="text-sm font-semibold text-gray-700 capitalize">
                                        {t('total_sale_price_label')} :

                                        <span className="font-normal text-gray-900">
                                            {totalSalePrice !== null
                                                ? itemData?.product?.rfq_currency_code +
                                                " " +
                                                totalSalePrice
                                                : t('na')}
                                        </span>
                                    </p>
                                }

                                {hasViewProductPurchasePricePermission &&
                                    <p className="text-sm font-semibold text-gray-700 capitalize">
                                        {t('total_purchase_price_label')} :

                                        <span className="font-normal text-gray-900">
                                            {totalPurchasePrice !== null
                                                ? itemData?.product?.rfq_currency_code +
                                                " " +
                                                totalPurchasePrice
                                                : t('na')}
                                        </span>
                                    </p>
                                }

                            </div>
                        </div>
                    }

                    {!itemData?.sale_price && (
                        <p className="text-red-500 text-xs mt-1">
                            {t('sale_price_required_for_update')}
                        </p>
                    )}

                    {/* --- Margin Inputs --- */}
                    <div className="border p-4 rounded-lg">

                        <h3 className="text-lg font-bold text-gray-800 mb-4 capitalize">
                            {t('margin_label')}
                        </h3>

                        <div className="flex gap-4">

                            {/* Flat Margin */}
                            {/* <div className="flex-1">
                                <label className="block text-sm font-medium mb-2 text-blue-gray-500">
                                    Flat Margin
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="any"
                                    {...register("flat_margin")}
                                    className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-yellow-500 ${errors.flat_margin ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="Flat"
                                    onBlur={() => handleSubmit((data) => onSubmit(data, 'flat_margin'))()}
                                />
                                {errors.flat_margin && (
                                    <p className="text-red-600 text-xs mt-1">
                                        {errors.flat_margin.message}
                                    </p>
                                )}
                            </div> */}

                            <div className="flex-1">

                                <label className="block text-sm font-medium mb-2 text-blue-gray-500 capitalize">
                                    {t('flat_margin_label')}
                                </label>

                                <div className="flex gap-2">

                                    <input
                                        type="number"
                                        {...register("flat_margin")}
                                        className={`w-full p-3 border rounded-md ${errors.flat_margin
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                        onFocus={() => {
                                            setActiveField("flat_margin");
                                            reset({ ...price });
                                        }}
                                    />

                                    {activeField === "flat_margin" &&
                                        watch("flat_margin") !== price.flat_margin && (
                                            <Button
                                                type="button"
                                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                                                onClick={() => {
                                                    handleSubmit(
                                                        (data) =>
                                                            onSubmit(
                                                                data,
                                                                "flat_margin"
                                                            )
                                                    )();

                                                    setActiveField(null);
                                                }}
                                            >
                                                {ct('save')}
                                            </Button>
                                        )}

                                </div>

                                {errors.flat_margin && (
                                    <p className="text-red-600 text-xs mt-1">
                                        {translateValidationError(
                                            errors.flat_margin.message
                                        )}
                                    </p>
                                )}

                            </div>

                            {/* Percentage Margin */}
                            <div className="flex-1">

                                <label className="block text-sm font-medium mb-2 text-blue-gray-500 capitalize">
                                    {t('percentage_margin_label')}
                                </label>

                                <div className="flex gap-2">

                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        {...register("percentage_margin")}
                                        className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-yellow-500 ${errors.percentage_margin
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                        placeholder={t('percentage_label')}
                                        onFocus={() => {
                                            setActiveField("percentage_margin");
                                            reset({ ...price });
                                        }}
                                    />

                                    {activeField === "percentage_margin" &&
                                        watch("percentage_margin") !== price.percentage_margin && (
                                            <Button
                                                type="button"
                                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                                                onClick={() =>
                                                    handleSubmit(
                                                        (data) =>
                                                            onSubmit(
                                                                data,
                                                                "percentage_margin"
                                                            )
                                                    )()
                                                }
                                            >
                                                {ct('save')}
                                            </Button>
                                        )}

                                </div>

                                {errors.percentage_margin && (
                                    <p className="text-red-600 text-xs mt-1">
                                        {translateValidationError(
                                            errors.percentage_margin.message
                                        )}
                                    </p>
                                )}

                            </div>

                        </div>
                    </div>

                    {/* --- Discount Inputs --- */}
                    <div className="border p-4 rounded-lg">

                        <h3 className="text-lg font-bold text-gray-800 mb-4 capitalize">
                            {t('discount_label')}
                        </h3>

                        <div className="flex gap-4">

                            {/* Flat Discount */}
                            <div className="flex-1">

                                <label className="block text-sm font-medium mb-2 text-blue-gray-500 capitalize">
                                    {t('flat_discount_label')}
                                </label>

                                <div className="flex gap-2">

                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        {...register("flat_discount")}
                                        className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-yellow-500 ${errors.flat_discount
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                        placeholder={t('flat_label')}
                                        onFocus={() => {
                                            setActiveField("flat_discount");
                                            reset({ ...price });
                                        }}
                                    />

                                    {activeField === "flat_discount" &&
                                        watch("flat_discount") !== price.flat_discount && (
                                            <Button
                                                type="button"
                                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                                                onClick={() =>
                                                    handleSubmit(
                                                        (data) =>
                                                            onSubmit(
                                                                data,
                                                                "flat_discount"
                                                            )
                                                    )()
                                                }
                                            >
                                                {ct('save')}
                                            </Button>
                                        )}

                                </div>

                                {errors.flat_discount && (
                                    <p className="text-red-600 text-xs mt-1">
                                        {translateValidationError(
                                            errors.flat_discount.message
                                        )}
                                    </p>
                                )}

                            </div>

                            {/* Percentage Discount */}
                            <div className="flex-1">

                                <label className="block text-sm font-medium mb-2 text-blue-gray-500 capitalize">
                                    {t('percentage_discount_label')}
                                </label>

                                <div className="flex gap-2">

                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        {...register("percentage_discount")}
                                        className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-yellow-500 ${errors.percentage_discount
                                            ? 'border-red-500'
                                            : 'border-gray-300'
                                            }`}
                                        placeholder={t('percentage_label')}
                                        onFocus={() => {
                                            setActiveField("percentage_discount");
                                            reset({ ...price });
                                        }}
                                    />

                                    {activeField === "percentage_discount" &&
                                        watch("percentage_discount") !== price.percentage_discount && (
                                            <Button
                                                type="button"
                                                className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                                                onClick={() =>
                                                    handleSubmit(
                                                        (data) =>
                                                            onSubmit(
                                                                data,
                                                                "percentage_discount"
                                                            )
                                                    )()
                                                }
                                            >
                                                {ct('save')}
                                            </Button>
                                        )}

                                </div>

                                {errors.percentage_discount && (
                                    <p className="text-red-600 text-xs mt-1">
                                        {translateValidationError(
                                            errors.percentage_discount.message
                                        )}
                                    </p>
                                )}

                            </div>

                        </div>
                    </div>

                    {/* --- Customer Price Input --- */}
                    <div>

                        <label className="block text-sm font-medium mb-2 capitalize">
                            {t('final_customer_price_label')}

                            <span className="text-red-600">*</span>
                        </label>

                        <div className="flex gap-2">

                            <input
                                type="number"
                                min="0"
                                step="any"
                                {...register("customer_price")}
                                className={`w-full p-3 border rounded-md focus:ring-2 focus:ring-yellow-500 ${errors.customer_price
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                    }`}
                                placeholder={t('price_label')}
                                disabled={!itemData?.sale_price}
                                onFocus={() => {
                                    setActiveField("customer_price");
                                    reset({ ...price });
                                }}
                            />

                            {activeField === "customer_price" &&
                                watch("customer_price") !== price.customer_price && (
                                    <Button
                                        type="button"
                                        className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material"
                                        onClick={() =>
                                            handleSubmit(
                                                (data) =>
                                                    onSubmit(
                                                        data,
                                                        "customer_price"
                                                    )
                                            )()
                                        }
                                    >
                                        {ct('save')}
                                    </Button>
                                )}

                        </div>

                        {errors.customer_price && (
                            <p className="text-red-600 text-sm mt-1">
                                {translateValidationError(
                                    errors.customer_price.message
                                )}
                            </p>
                        )}

                    </div>

                </DialogBody>

                {/* Footer */}
                <DialogFooter className="flex justify-end gap-3 border-t px-6 py-4">
                    {/* <button
                        type="button"
                        onClick={handleClose}
                        className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-md font-semibold hover:bg-gray-300 transition"
                        disabled={loading}
                    >
                        Cancel
                    </button> */}

                    {/* <button
                        type="submit"
                        className="px-5 py-2.5 bg-yellow-500 text-white rounded-md font-semibold hover:bg-yellow-600 transition"
                        disabled={loading || !itemData?.vendor_price}
                    >
                        {loading ? 'Updating...' : 'Update Price'}
                    </button> */}
                </DialogFooter>

            </form>
        </Dialog>
    );
}

export default UpdatePriceModal;